import React, { PropsWithChildren } from "react";
import { useState } from "react";
import { useEffect } from "react";
import axios from "axios";
import CurrentTemperature from "@appsinti/common-utils/interfaces/weather/CurrentTemperature";
import HourTemperature from "@appsinti/common-utils/interfaces/weather/HourTemperature";
import DayTemperature from "@appsinti/common-utils/interfaces/weather/DayTemperature";
import { BACKEND } from "@appsinti/common-utils/constants/urls";
import auth from "../resources/api/auth";

export type DataContextType = {
  loading: boolean;
  current: CurrentTemperature | null;
  hourly: HourTemperature[] | null;
  daily: DayTemperature[] | null;
  weatherCamPreviewURL: string | null;
  lastUpdate: Date;
  fetchData?: () => void;
};

const DataContext = React.createContext<DataContextType>({
  loading: false,
  current: null,
  hourly: null,
  daily: null,
  lastUpdate: new Date(),
  weatherCamPreviewURL: null,
  fetchData: () => {}
});

export const useData = () => React.useContext(DataContext);
const { Authorization } = auth;

export const DataProvider: React.FC<PropsWithChildren<{
  appId: number;
}>> = ({ appId, children }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [current, setCurrent] = useState<CurrentTemperature | null>(null);
  const [hourly, setHourly] = useState<HourTemperature[] | null>(null);
  const [daily, setDaily] = useState<DayTemperature[] | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [weatherCamPreviewURL, setWeatherCamPreviewURL] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const current = await axios.get<CurrentTemperature>(
      `${BACKEND}/appsinti/weather/current`, { headers: { "app-id": appId, Authorization } }
    );
    setCurrent(current.data);
    const hourly = await axios.get<HourTemperature[]>(
      `${BACKEND}/appsinti/weather/hourly`, { headers: { "app-id": appId, Authorization } }
    );
    setHourly(hourly.data);
    const daily = await axios.get<DayTemperature[]>(
      `${BACKEND}/appsinti/weather/daily`, { headers: { "app-id": appId, Authorization } }
    );
    const date = new Date();
    setWeatherCamPreviewURL(current.data.weatherCamPreviewURL ? `${current.data.weatherCamPreviewURL}?t=${date.getTime()}` : null);
    setDaily(daily.data);
    setLastUpdate(date);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  return (
    <DataContext.Provider
      value={{
        loading,
        current,
        hourly,
        daily,
        lastUpdate,
        weatherCamPreviewURL,
        fetchData,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
